import { LoginTypes, loginUser } from '@api/login';
import checkYourInboxImg from '@assets/images/check-inbox.svg';
import Card from '@components/Atoms/Card';
import Img from '@components/Atoms/Img';
import LinkText from '@components/Atoms/Link';
import TokenInput from '@components/Organisms/TokenInput';
import { buildLoginPath } from '@helpers/auth.helper';
import useIsMobileApp from '@hooks/useIsMobileApp';
import useWindowDimensions from '@hooks/useWindowDimensions';
import AuthenticationLayout from '@layouts/AuthenticationLayout';
import { showToast } from '@shared/atoms';
import { formatTime } from '@utils/date.util';
import { ToastMessages } from 'constants/toastMessages';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styles from './index.module.scss';

const CheckYourInbox: React.FunctionComponent<CheckYourInboxProps> = (props) => {
  const {
    organizationLogo,
    organizationAltText,
    companyURL,
    email,
    hideHapstarLogo,
    platformName,
    organizationId,
  } = props;
  const { isMobile } = useWindowDimensions();
  const isMobileApp = useIsMobileApp();
  const history = useHistory();
  const [emailResendCount, setEmailResendCount] = useState(0);
  const hideHapstarLogoEnabled = hideHapstarLogo;
  const [emailSending, setEmailSending] = useState(false);
  const [coolDownTime, setCoolDownTime] = useState(120);
  const rememberMe = localStorage.getItem('rememberMe');
  const validatedRememberMe = Boolean(rememberMe && rememberMe === 'true') || false;

  let type = LoginTypes.LINK;
  if (isMobileApp) {
    type = LoginTypes.TOKEN;
  }

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (coolDownTime > 0) {
      timer = setInterval(() => {
        setCoolDownTime((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [coolDownTime]);

  const handleResendEmail = async () => {
    if (emailResendCount < 3) {
      setEmailSending(true);
      try {
        const response = await loginUser({
          email: email.toLowerCase(),
          type,
          organizationId,
          remember: validatedRememberMe,
        });

        showToast(ToastMessages.auth.info.EMAIL_SENT_TO_YOUR_INBOX, undefined, 'success');

        setCoolDownTime(120);
      } catch (err: any) {
        showToast(err.json.message, undefined, 'error');
      } finally {
        setEmailSending(false);
      }
    } else {
      showToast(ToastMessages.common.PLEASE_TRY_AGAIN_AFTER_FEW_HOURS, undefined, 'warning');
    }

    setEmailResendCount((preState) => preState + 1);
  };

  const handleOnBack = async () => {
    history.push(
      buildLoginPath({
        companyURL,
        isMobileApp,
      }),
    );
  };

  return (
    <AuthenticationLayout
      title="CheckYourInbox"
      organizationLogo={organizationLogo}
      organizationAltText={organizationAltText}
      hideHapstarLogoEnabled={hideHapstarLogoEnabled}
    >
      <div className={styles.cardContainer}>
        <Card>
          <div className={styles.checkInboxContentContainer}>
            <div className={styles.checkInboxContent}>
              {organizationLogo && isMobile && !hideHapstarLogoEnabled && (
                <div className={styles.organizationLogoHolder}>
                  <Img alt={organizationAltText || 'Organization logo'} src={organizationLogo} />
                </div>
              )}
              <Img src={checkYourInboxImg} alt="Check you inbox" className={styles.img} />
              {isMobileApp ? (
                <div className={styles.contentContainer}>
                  <h1>Check your email</h1>
                  <p>We have sent an activation email to: {email}</p>
                  <p>Enter the verification code below.</p>
                  <div className={styles.token}>
                    <TokenInput email={email} />
                  </div>
                </div>
              ) : (
                <div className={styles.contentContainer}>
                  <h1>Check your email to confirm your account</h1>
                </div>
              )}
              <p className={styles.resendText}>
                Didn&apos;t receive an email?{' '}
                {coolDownTime > 0 ? (
                  <span className={styles.timerContainer}>
                    <span className={styles.resendLabel}>Resend in </span>
                    <span className={styles.timer}>{formatTime(coolDownTime)}</span>
                  </span>
                ) : (
                  <LinkText
                    onClick={handleResendEmail}
                    disable={emailResendCount > 3 || coolDownTime > 0 || emailSending}
                  >
                    Resend
                  </LinkText>
                )}
              </p>
              <LinkText onClick={handleOnBack}>Back</LinkText>
            </div>
          </div>
        </Card>
      </div>
    </AuthenticationLayout>
  );
};

CheckYourInbox.displayName = 'CheckYourInbox';

CheckYourInbox.defaultProps = {
  organizationLogo: undefined,
  organizationAltText: undefined,
  companyURL: undefined,
  hideHapstarLogo: false,
  platformName: null,
};

interface CheckYourInboxProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  email: string;
  organizationLogo?: string | undefined;
  organizationAltText?: string | undefined;
  companyURL?: string;
  hideHapstarLogo?: boolean;
  platformName?: string | null;
  organizationId: number;
}

export default CheckYourInbox;
